import {Color} from '@benchling/nucleus-ui';
import {Loader, Popover, ScrollArea, TextInput, UnstyledButton, createStyles} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import FolderDAO from 'file-browser/FolderDAO';
import {fetchUserTypeahead, type UserTypeaheadItem} from 'omnibox/OmniboxDAO';
import type {FilterTreeNode} from 'omnibox/omniboxFilterTree';
import {buildFolderTreeNodes} from 'omnibox/omniboxFolderTree';
import {pillSx, usePillStyles} from 'omnibox/omniboxPillStyles';
import OmniboxTreeFilterPill from 'omnibox/OmniboxTreeFilterPill';
import type {OmniboxFilter} from 'omnibox/useTypeaheadData';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as logging from 'util/Logging';

// Entry and Entity are mutually exclusive one-click type toggles; "type" (Type), "project" (Project
// or Folder), and "user" (Authored By) each open a dropdown.
export type OmniboxFilterTabId = 'entry' | 'entity' | 'type' | 'user' | 'project';

const TYPE_TABS: ReadonlyArray<{id: 'entry' | 'entity'; label: string; icon: BenchlingIconName}> = [
  {id: 'entry', label: 'Entry', icon: 'file-lines-outline'},
  {id: 'entity', label: 'Entity', icon: 'square-list-outline'},
];

const useStyles = createStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box' as const,
    // Fixed-height row inside the omnibox's flex column: never shrink when results fill the modal to
    // its max-height, or the pills row gets squeezed upward as results land.
    flexShrink: 0,
    height: 30,
    padding: '0px 16px 8px',
    gap: 8,
    backgroundColor: theme.white,
    // Side borders only, per Figma (Frame 1000002826).
    borderLeft: `1px solid ${Color.GRAY_400}`,
    borderRight: `1px solid ${Color.GRAY_400}`,
  },
  // Group 38: the pill group, left-aligned within the row.
  pillGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 22,
  },
  dropdown: {
    padding: 0,
  },
  // Sized to the widest author row so names and handles show in full, with a floor that keeps the
  // loading and empty states from collapsing and a cap past which long rows truncate.
  authorDropdown: {
    minWidth: 260,
    maxWidth: 400,
  },
  searchWrapper: {
    padding: 8,
    borderBottom: `1px solid ${Color.GRAY_150}`,
  },
  // ScrollArea.Autosize wraps its content in a flex column that would otherwise grow to fit the
  // widest row, pushing long names, handles, and the scrollbar past the dropdown's max width.
  autosizeScrollArea: {
    '& > div': {
      minWidth: 0,
    },
  },
  // The scroll viewport lays its content out as a table, which also sizes to the widest row; a
  // block fills the viewport width so long rows truncate instead.
  scrollViewport: {
    '& > div': {
      display: 'block !important',
    },
  },
  optionList: {
    padding: 4,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '6px 8px',
    borderRadius: theme.radius.sm,
    fontSize: 13,
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  optionName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  optionHandle: {
    color: Color.GRAY_600,
    fontSize: 11,
    maxWidth: '50%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  optionCheck: {
    flexShrink: 0,
    color: theme.colors.blue[6],
    width: 14,
    fontSize: 12,
  },
  emptyOption: {
    padding: '8px 12px',
    fontSize: 12,
    color: Color.GRAY_600,
    textAlign: 'center' as const,
  },
  loadingOption: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 12px',
  },
}));

interface AuthoredByPillProps {
  isActive: boolean;
  selectedAuthors: ReadonlyArray<OmniboxFilter>;
  onToggleAuthor: (author: OmniboxFilter) => void;
}

function AuthoredByPill({isActive, selectedAuthors, onToggleAuthor}: AuthoredByPillProps): JSX.Element {
  const {classes, cx} = useStyles();
  const {classes: pillClasses} = usePillStyles();
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  const selectedAuthorIds = useMemo(() => new Set(selectedAuthors.map((a) => a.id)), [selectedAuthors]);

  // Author filtering is single-select, so the pill reflects the one chosen author inline instead of
  // rendering a separate filter chip. Falls back to the plain "Authored By" label when none is set.
  const pillLabel =
    selectedAuthors.length === 1
      ? `Authored by: ${selectedAuthors[0].isSelf ? 'You' : selectedAuthors[0].name}`
      : 'Authored By';

  // populate options when a user starts typing in the search input, debounced to avoid excessive API calls
  const [search, setSearch] = useState('');
  const [debouncedSearchValue] = useDebouncedValue(search, 300);
  const [options, setOptions] = useState<Array<UserTypeaheadItem>>([]);

  useEffect(() => {
    if (!opened) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchUserTypeahead(debouncedSearchValue.trim())
      .then((users: Array<UserTypeaheadItem>) => {
        if (!cancelled) {
          setOptions(users);
        }
      })
      .catch((e) => {
        // Drop stale results on failure so a prior query's users can't be shown (or selected)
        // under the current search text.
        if (!cancelled) {
          setOptions([]);
        }
        // eslint-disable-next-line no-console
        console.error('Failed to fetch users in typeahead', e);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchValue, opened]);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withinPortal={true}
      zIndex={1056} // The omnibox Modal renders at z-Index 1055; the portaled dropdown must sit above it (matching OmniboxFooter's Popover).
      shadow="md"
      radius="md"
    >
      <Popover.Target>
        <UnstyledButton
          aria-pressed={isActive}
          aria-haspopup="listbox"
          aria-expanded={opened}
          onClick={() => {
            setOpened((o) => !o);
          }}
          sx={pillSx(isActive)}
        >
          <span className={pillClasses.iconWrapper}>
            <BenchlingIcon icon="image-polaroid-user" className={pillClasses.pillIcon} aria-hidden="true" />
          </span>
          {pillLabel}
          <BenchlingIcon icon="chevron-down" className={pillClasses.pillChevron} aria-hidden="true" />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className={cx(classes.dropdown, classes.authorDropdown)}>
        <div className={classes.searchWrapper}>
          <TextInput
            size="xs"
            placeholder="Filter by author..."
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
            }}
            autoFocus={true}
            aria-label="Filter by author"
          />
        </div>
        <ScrollArea.Autosize
          mah={240}
          type="auto"
          className={classes.autosizeScrollArea}
          classNames={{viewport: classes.scrollViewport}}
        >
          <div className={classes.optionList} role="listbox" aria-label="Authors">
            {loading ? (
              <div className={classes.loadingOption} role="status" aria-label="Loading authors">
                <Loader size="xs" />
              </div>
            ) : options.length === 0 ? (
              <div className={classes.emptyOption}>No matching people</div>
            ) : (
              options.map((author) => {
                const selected = selectedAuthorIds.has(author.apiId);
                return (
                  <UnstyledButton
                    key={author.apiId}
                    role="option"
                    aria-selected={selected}
                    className={classes.option}
                    onClick={() => {
                      onToggleAuthor({
                        kind: 'user',
                        id: author.apiId,
                        name: author.name,
                        handle: author.handle,
                        isSelf: author.isSelf,
                      });
                    }}
                  >
                    <span className={classes.optionName}>{author.isSelf ? 'You' : author.name}</span>
                    {author.handle && <span className={classes.optionHandle}>@{author.handle}</span>}
                    {selected && (
                      <BenchlingIcon icon="check" className={classes.optionCheck} aria-hidden="true" />
                    )}
                  </UnstyledButton>
                );
              })
            )}
          </div>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}

interface ProjectFolderPillProps {
  isActive: boolean;
  anchorRef: React.RefObject<HTMLDivElement>;
  selectedProjectFolders: ReadonlyArray<OmniboxFilter>;
  onToggleProjectFolder: (item: OmniboxFilter) => void;
}

function ProjectFolderPill({
  isActive,
  anchorRef,
  selectedProjectFolders,
  onToggleProjectFolder,
}: ProjectFolderPillProps): JSX.Element {
  const selectedProjectFolder = selectedProjectFolders.length === 1 ? selectedProjectFolders[0] : null;
  const label = selectedProjectFolder
    ? `${selectedProjectFolder.kind === 'folder' ? 'In folder' : 'In project'}: ${selectedProjectFolder.name}`
    : 'Folder';

  // Every readable folder loads once through the session-wide folder cache the sidepanel folder
  // filter also uses, so the tree, search, and expand all work client-side without further requests.
  // Fetched on mount rather than on open so the dropdown is usually ready by the time it opens.
  const [folderNodes, setFolderNodes] = useState<Array<FilterTreeNode> | null>(null);
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(FolderDAO.fetchAll({level: 'read'}))
      .then((folders) => {
        if (!cancelled) {
          setFolderNodes(buildFolderTreeNodes(folders ?? []));
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setFolderNodes([]);
        }
        logging.error('Failed to fetch folders for the omnibox folder filter', {extra: {error: e}});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <OmniboxTreeFilterPill
      label={label}
      icon="folder"
      isActive={isActive}
      anchorRef={anchorRef}
      nodes={folderNodes}
      selectedFilters={selectedProjectFolders}
      onSelect={onToggleProjectFolder}
      searchPlaceholder="Filter by project or folder..."
      searchLabel="Filter by project or folder"
      listLabel="Projects and folders"
      loadingLabel="Loading projects and folders"
      emptyMessage="No matching projects or folders"
    />
  );
}

interface TypePillProps {
  isActive: boolean;
  anchorRef: React.RefObject<HTMLDivElement>;
  // Types and their schemas, grouped by category; null until the schemas load.
  typeTreeNodes: ReadonlyArray<FilterTreeNode> | null;
  selectedType: OmniboxFilter | null;
  onSelectType: (item: OmniboxFilter) => void;
}

function TypePill({
  isActive,
  anchorRef,
  typeTreeNodes,
  selectedType,
  onSelectType,
}: TypePillProps): JSX.Element {
  const label = selectedType
    ? `${selectedType.kind === 'schema' ? 'Schema' : 'Type'}: ${selectedType.name}`
    : 'Type';
  const selectedFilters = useMemo(() => (selectedType ? [selectedType] : []), [selectedType]);
  return (
    <OmniboxTreeFilterPill
      label={label}
      icon="shapes-outline"
      isActive={isActive}
      anchorRef={anchorRef}
      nodes={typeTreeNodes}
      selectedFilters={selectedFilters}
      onSelect={onSelectType}
      searchPlaceholder="Filter by type or schema..."
      searchLabel="Filter by type or schema"
      listLabel="Types and schemas"
      loadingLabel="Loading types and schemas"
      emptyMessage="No matching types or schemas"
    />
  );
}

interface OmniboxFilterTabsProps {
  activeTabIds: ReadonlySet<OmniboxFilterTabId>;
  onTabClick: (id: 'entry' | 'entity') => void;
  typeTreeNodes: ReadonlyArray<FilterTreeNode> | null;
  selectedType: OmniboxFilter | null;
  onSelectType: (item: OmniboxFilter) => void;
  showAuthoredBy: boolean;
  selectedAuthors: ReadonlyArray<OmniboxFilter>;
  onToggleAuthor: (author: OmniboxFilter) => void;
  selectedProjectFolders: ReadonlyArray<OmniboxFilter>;
  onToggleProjectFolder: (item: OmniboxFilter) => void;
  className?: string;
}

export default function OmniboxFilterTabs({
  activeTabIds,
  onTabClick,
  typeTreeNodes,
  selectedType,
  onSelectType,
  showAuthoredBy,
  selectedAuthors,
  onToggleAuthor,
  selectedProjectFolders,
  onToggleProjectFolder,
  className,
}: OmniboxFilterTabsProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const {classes: pillClasses} = usePillStyles();
  const rootRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={rootRef} className={className ?? classes.root}>
      <div role="group" aria-label="Filter search results by type" className={classes.pillGroup}>
        {TYPE_TABS.map((tab) => {
          const isActive = activeTabIds.has(tab.id);
          return (
            <UnstyledButton
              key={tab.id}
              aria-pressed={isActive}
              onClick={() => {
                onTabClick(tab.id);
              }}
              sx={pillSx(isActive)}
            >
              <span className={pillClasses.iconWrapper}>
                <BenchlingIcon icon={tab.icon} className={pillClasses.pillIcon} aria-hidden="true" />
              </span>
              {tab.label}
            </UnstyledButton>
          );
        })}
        <TypePill
          anchorRef={rootRef}
          isActive={activeTabIds.has('type')}
          typeTreeNodes={typeTreeNodes}
          selectedType={selectedType}
          onSelectType={onSelectType}
        />
        <ProjectFolderPill
          anchorRef={rootRef}
          isActive={activeTabIds.has('project')}
          selectedProjectFolders={selectedProjectFolders}
          onToggleProjectFolder={onToggleProjectFolder}
        />
        {showAuthoredBy && (
          <AuthoredByPill
            isActive={activeTabIds.has('user')}
            selectedAuthors={selectedAuthors}
            onToggleAuthor={onToggleAuthor}
          />
        )}
      </div>
    </div>
  );
}
